import './index.scss';

import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';

import {
	MANUAL_TEST_EMAIL,
	MANUAL_TEST_PASSWORD
} from '../../devManualTestAccount';
import {APP_LOGIN_BADGE_URL} from '../../tools';
import {FOOTER_URL_LINK, PAGE_FULL_TITLE} from '../../constants/strings';

const SHOW_DEV_EMULATOR_SIGN_IN = process.env.REACT_APP_E2E_EMULATOR_SIGNIN === 'true';

export const LOGIN_RESEND_COOLDOWN_SECONDS = process.env.NODE_ENV === 'test' ? 2 : 45;

export const LOGIN_EMAIL_PLACEHOLDER_EXAMPLE = 'your@email.com';

export const LOGIN_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isLoginEmailValid = (value) => LOGIN_EMAIL_REGEX.test(String(value).trim());

export const isLoginEmailSubmittable = (value) => {
	const trimmed = String(value).trim();
	return (
		isLoginEmailValid(value) &&
		trimmed.toLowerCase() !== LOGIN_EMAIL_PLACEHOLDER_EXAMPLE
	);
};

const MailIcon = () => (
	<svg className="login-page__input-icon" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
		<path
			fill="currentColor"
			d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"
		/>
	</svg>
);

const CheckIcon = () => (
	<svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
		<path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
	</svg>
);

const LoginWallpaper = ({
	onRequestEmailSignIn,
	onCompleteEmailLinkWithEmail,
	emailLinkNeedsEmail,
	onDismissEmailLinkPrompt
}) => {
	const [email, setEmail] = useState('');
	const [localError, setLocalError] = useState('');
	const [linkSent, setLinkSent] = useState(false);
	const [resendSecondsLeft, setResendSecondsLeft] = useState(0);
	const [isResending, setIsResending] = useState(false);
	const [isDevSigningIn, setIsDevSigningIn] = useState(false);

	useEffect(() => {
		if (emailLinkNeedsEmail) {
			setLinkSent(false);
			setResendSecondsLeft(0);
		}
	}, [emailLinkNeedsEmail]);

	useEffect(() => {
		if (resendSecondsLeft <= 0) return undefined;
		const id = globalThis.window.setTimeout(() => {
			setResendSecondsLeft((s) => (s <= 1 ? 0 : s - 1));
		}, 1000);
		return () => globalThis.window.clearTimeout(id);
	}, [resendSecondsLeft]);

	const handleChange = (event) => {
		setLocalError('');
		setEmail(event.target.value);
	};

	const handleSendLink = async (event) => {
		event.preventDefault();
		setLocalError('');
		if (isLoginEmailValid(email)) {
			if (email.trim().toLowerCase() === LOGIN_EMAIL_PLACEHOLDER_EXAMPLE) {
				setLocalError('Use your own email address.');
				return;
			}
			const result = await onRequestEmailSignIn(email.trim());
			if (result?.ok) {
				setLinkSent(true);
				setResendSecondsLeft(LOGIN_RESEND_COOLDOWN_SECONDS);
			}
		} else {
			setLocalError(email.trim() ? 'Enter a valid email.' : 'Email required.');
		}
	};

	const handleResend = async () => {
		if (!email.trim() || resendSecondsLeft > 0 || isResending) return;
		setLocalError('');
		setIsResending(true);
		const result = await onRequestEmailSignIn(email.trim());
		setIsResending(false);
		if (result?.ok) {
			setResendSecondsLeft(LOGIN_RESEND_COOLDOWN_SECONDS);
		}
	};

	const handleEditEmail = () => {
		setLinkSent(false);
		setResendSecondsLeft(0);
		setLocalError('');
	};

	const handleCompleteLink = async (event) => {
		event.preventDefault();
		setLocalError('');
		if (isLoginEmailValid(email)) {
			if (email.trim().toLowerCase() === LOGIN_EMAIL_PLACEHOLDER_EXAMPLE) {
				setLocalError('Use your own email address.');
				return;
			}
			await onCompleteEmailLinkWithEmail(email.trim());
		} else {
			setLocalError(
				email.trim()
					? 'Enter a valid email.'
					: 'Enter the email you used for this link.'
			);
		}
	};

	const handleDismissLinkPrompt = () => {
		setLocalError('');
		setEmail('');
		if (emailLinkNeedsEmail) {
			onDismissEmailLinkPrompt();
		}
	};

	/* istanbul ignore next */
	const handleDevEmulatorSignIn = async () => {
		const signIn = globalThis.window?.__linkspaceE2eEnsureUserAndSignIn;
		if (typeof signIn !== 'function') {
			setLocalError('Dev sign-in is unavailable. Start the app with npm run start:e2e.');
			return;
		}
		setLocalError('');
		setIsDevSigningIn(true);
		try {
			await signIn(MANUAL_TEST_EMAIL, MANUAL_TEST_PASSWORD);
		} catch (error) {
			setLocalError(error?.message || 'Dev sign-in failed.');
		} finally {
			setIsDevSigningIn(false);
		}
	};

	const currentYear = new Date().getFullYear();
	const canSubmitEmail = isLoginEmailSubmittable(email);
	const showSentPanel = linkSent && !emailLinkNeedsEmail;
	let resendLabel = 'Resend link';
	if (isResending) {
		resendLabel = 'Sending…';
	} else if (resendSecondsLeft > 0) {
		resendLabel = `Resend in ${resendSecondsLeft}s`;
	}

	return (
		<div className="login-page">
			<div className="login-page__glow login-page__glow--1" aria-hidden="true"/>
			<div className="login-page__glow login-page__glow--2" aria-hidden="true"/>

			<div className="header-overlay">
				<h1>{PAGE_FULL_TITLE}</h1>
			</div>

			<main className="login-page__main">
				<div className="login-card">
					{showSentPanel ? (
						<>
							<div className="login-card__intro login-card__intro--sent">
								<div className="login-card__header">
									<div
										className="login-card__badge login-card__badge--success"
										aria-hidden="true"
									>
										<CheckIcon/>
									</div>
									<div className="login-card__header-text">
										<h1 className="login-card__title">Check your email</h1>
										<p className="login-card__sent-email" title={email.trim()}>
											{email.trim()}
										</p>
									</div>
								</div>
								<p className="login-card__hint">
									A sign-in link has been sent to this address
									<br />
									Open the link in your inbox to sign in. Check spam if needed
								</p>
							</div>

							<button
								type="button"
								className="login-card__secondary-btn"
								onClick={handleResend}
								disabled={resendSecondsLeft > 0 || isResending}
							>
								{resendLabel}
							</button>
							<div className="login-card__footer">
								<button type="button" className="login-card__ghost" onClick={handleEditEmail}>
									Wrong email?
								</button>
							</div>
						</>
					) : (
						<>
							<div className="login-card__intro">
								<div className="login-card__intro-top">
									<div className="login-card__badge login-card__badge--mark-image" aria-hidden="true">
										<img
											className="login-card__logo-img"
											src={APP_LOGIN_BADGE_URL}
											alt=""
											decoding="async"
										/>
									</div>
									<div className="login-card__intro-copy">
										<h1 className="login-card__title">
											{emailLinkNeedsEmail ? (
												'Confirm email'
											) : (
												<span className="login-card__title-split">
													<span className="login-card__title-line login-card__title-line--lead">
														Your space for everything
													</span>
													<span className="login-card__title-line login-card__title-line--accent">
														worth keeping
													</span>
												</span>
											)}
										</h1>
										{!emailLinkNeedsEmail && (
											<p className="login-card__tagline">
												One place for what powers your day
											</p>
										)}
									</div>
								</div>
								<div className="login-card__lede-row">
									<div className="login-card__lede-anchor" aria-hidden="true"/>
									<p className="login-card__lede">
										{emailLinkNeedsEmail ? (
											'Same address as your invite link'
										) : (
											<>
												<span className="login-card__lede-line">
													Enter your email for a passwordless sign-in
												</span>
												<span className="login-card__lede-line">
													First visit? That same email will set up your account
												</span>
											</>
										)}
									</p>
								</div>
							</div>

							<form
								className="login-card__form"
								onSubmit={emailLinkNeedsEmail ? handleCompleteLink : handleSendLink}
								noValidate
							>
								<label className="login-card__label" htmlFor="login-email">
									Email
								</label>
								<div className="login-card__field">
									<span className="login-card__field-icon" aria-hidden="true">
										<MailIcon/>
									</span>
									<input
										id="login-email"
										type="email"
										name="email"
										value={email}
										onChange={handleChange}
										placeholder={LOGIN_EMAIL_PLACEHOLDER_EXAMPLE}
										autoComplete="email"
										inputMode="email"
										autoCapitalize="none"
										autoCorrect="off"
										spellCheck="false"
									/>
								</div>

								{localError && (
									<p className="login-card__error" role="alert">{localError}</p>
								)}

								<button type="submit" className="login-card__submit" disabled={!canSubmitEmail}>
									Continue
								</button>

							{/* istanbul ignore next */}
							{SHOW_DEV_EMULATOR_SIGN_IN && !emailLinkNeedsEmail && (
								<button
									type="button"
									className="login-card__secondary-btn"
									onClick={handleDevEmulatorSignIn}
									disabled={isDevSigningIn}
								>
									{isDevSigningIn ? 'Signing in…' : 'Sign in with local test account'}
								</button>
							)}

								{emailLinkNeedsEmail && (
									<button
										type="button"
										className="login-card__ghost"
										onClick={handleDismissLinkPrompt}
									>
										Cancel
									</button>
								)}
							</form>
						</>
					)}
				</div>
			</main>

			<footer className="login-page__foot">
				<a
					href={FOOTER_URL_LINK}
					target="_blank"
					rel="noopener noreferrer"
					className="login-page__foot-link"
				>
					© {currentYear} {PAGE_FULL_TITLE}
				</a>
			</footer>
		</div>
	);
};

LoginWallpaper.propTypes = {
	onRequestEmailSignIn: PropTypes.func.isRequired,
	onCompleteEmailLinkWithEmail: PropTypes.func.isRequired,
	emailLinkNeedsEmail: PropTypes.bool.isRequired,
	onDismissEmailLinkPrompt: PropTypes.func.isRequired
};

export default LoginWallpaper;
